import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { computeScore, grade } from '../lib/preservation';
import { buildShareImage, TONE_HEX } from '../lib/shareImage';
import kmgLogo from '../assets/kmg_full.png';

function ShareCard() {
  const { t } = useTranslation();
  const verdict = useSelector((s) => s.scan.verdict);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const flagged = verdict?.flaggedCount ?? 0;
  const total = verdict?.totalGames ?? 0;
  const score = computeScore(verdict || {});
  const g = grade(score);

  // Aucun jeu à risque -> "J'ai perdu 0 jeux" ne se partage pas.
  // On bascule sur la NOTE, comparable entre amis (marche même à 0).
  const useScore = flagged === 0;

  const line = useScore ? t('share.score_card', { grade: g.letter }) : t('share.card_line', { flagged });
  const sub = useScore ? t('share.score_sub') : t('share.card_sub');
  const shareText = useScore
    ? `${t('share.score_line', { grade: g.letter, score })} ${sub}`
    : `${t('share.card_line', { flagged })} ${sub}`;

  const handleShare = async () => {
    setBusy(true);
    const url = window.location.origin;
    try {
      const blob = await buildShareImage({
        mode: useScore ? 'score' : 'danger',
        toneHex: useScore ? TONE_HEX[g.tone] || TONE_HEX.danger : TONE_HEX.danger,
        logoSrc: kmgLogo,
        grade: g.letter,
        score,
        flagged,
        total,
        // Mode note : étiquette "score" (le nombre est une note) + défi de comparaison.
        eyebrow: t('share.score_eyebrow'),
        line: useScore ? '' : line,
        sub: useScore ? t('share.score_sub') : sub,
        url: 'keepmygames.com',
      });
      const file = blob ? new File([blob], 'keepmygames.png', { type: 'image/png' }) : null;

      // Mobile : partage natif de l'IMAGE (avec le lien en légende)
      if (file && navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        try {
          await navigator.share({ title: t('brand'), text: `${shareText} ${url}`, files: [file] });
          setBusy(false);
          return;
        } catch (_) {
          /* annulé -> on tombe sur le téléchargement */
        }
      }

      // Ordi : télécharge l'image + copie le lien
      if (blob) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'keepmygames.png';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      }
      try {
        await navigator.clipboard.writeText(`${shareText} ${url}`);
      } catch (_) {
        /* rien à faire */
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (_) {
      // Dernier recours : partage texte simple
      try {
        await navigator.clipboard.writeText(`${shareText} ${url}`);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (__) {
        /* rien à faire */
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="share">
      <div className={`share-card ${useScore ? `share-card--grade share-card--${g.tone}` : ''}`}>
        {useScore ? (
          <>
            <div className="share-card__figure">
              <span className="share-card__grade">{g.letter}</span>
              {score}
              <span className="share-card__figure-total">/ 100</span>
            </div>
            <p className="share-card__sub">{sub}</p>
          </>
        ) : (
          <>
            <div className="share-card__figure">
              {flagged}
              <span className="share-card__figure-total">/ {total}</span>
            </div>
            <p className="share-card__line">{t('share.card_line', { flagged })}</p>
            <p className="share-card__sub">{sub}</p>
          </>
        )}
      </div>

      <div className="share__cta">
        <h2>{t('share.title')}</h2>
        <p>{t('share.subtitle')}</p>
        <button type="button" className="btn btn--amber" onClick={handleShare} disabled={busy}>
          {busy ? '…' : copied ? t('share.image_saved') : t('share.button')}
        </button>
      </div>
    </section>
  );
}

export default ShareCard;
